<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  socialProfiles: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const SOCIAL_PLATFORMS = [
  {
    key: 'linkedin',
    icon: 'i-ri-linkedin-box-fill',
    prefix: 'https://linkedin.com/',
  },
  {
    key: 'facebook',
    icon: 'i-ri-facebook-circle-fill',
    prefix: 'https://facebook.com/',
  },
  {
    key: 'instagram',
    icon: 'i-ri-instagram-line',
    prefix: 'https://instagram.com/',
  },
  {
    key: 'telegram',
    icon: 'i-ri-telegram-fill',
    prefix: 'https://t.me/',
  },
  {
    key: 'tiktok',
    icon: 'i-ri-tiktok-fill',
    prefix: 'https://tiktok.com/@',
  },
  {
    key: 'twitter',
    icon: 'i-ri-twitter-x-fill',
    prefix: 'https://x.com/',
  },
  {
    key: 'github',
    icon: 'i-ri-github-fill',
    prefix: 'https://github.com/',
  },
  {
    key: 'discord',
    icon: 'i-ri-discord-fill',
    prefix: 'https://discord.com/users/',
  },
];

const isAdding = ref(false);
const selectedPlatform = ref('');
const profileValue = ref('');
const editingPlatform = ref('');
const editingValue = ref('');
const profilePendingDeletion = ref(null);
const deleteDialogRef = ref(null);

const platformLabel = key =>
  t(`CONVERSATION_SIDEBAR.SOCIAL_PROFILES.PLATFORMS.${key.toUpperCase()}`);

const configuredProfiles = computed(() =>
  SOCIAL_PLATFORMS.filter(({ key }) => props.socialProfiles[key]?.trim()).map(
    platform => ({
      ...platform,
      value: props.socialProfiles[platform.key].trim(),
    })
  )
);

const availablePlatforms = computed(() =>
  SOCIAL_PLATFORMS.filter(({ key }) => !props.socialProfiles[key]?.trim()).map(
    ({ key }) => ({
      label: platformLabel(key),
      value: key,
    })
  )
);

const profileLink = ({ key, prefix, value }) => {
  if (/^https?:\/\//i.test(value)) return value;
  if (key === 'discord' && !/^\d+$/.test(value)) return '';
  return `${prefix}${value.replace(/^@/, '')}`;
};

const resetForm = () => {
  isAdding.value = false;
  selectedPlatform.value = '';
  profileValue.value = '';
};

const startAdding = () => {
  selectedPlatform.value = availablePlatforms.value[0]?.value || '';
  profileValue.value = '';
  isAdding.value = true;
};

const addProfile = () => {
  const value = profileValue.value.trim();
  if (!selectedPlatform.value || !value) return;

  emit('update', {
    ...props.socialProfiles,
    [selectedPlatform.value]: value,
  });
  resetForm();
};

const startEditing = profile => {
  editingPlatform.value = profile.key;
  editingValue.value = profile.value;
  resetForm();
};

const cancelEditing = () => {
  editingPlatform.value = '';
  editingValue.value = '';
};

const saveProfile = () => {
  const value = editingValue.value.trim();
  if (!editingPlatform.value || !value) return;

  emit('update', {
    ...props.socialProfiles,
    [editingPlatform.value]: value,
  });
  cancelEditing();
};

const requestDeletion = profile => {
  profilePendingDeletion.value = profile;
  deleteDialogRef.value?.open();
};

const deleteProfile = () => {
  if (!profilePendingDeletion.value) return;

  const profiles = { ...props.socialProfiles };
  delete profiles[profilePendingDeletion.value.key];
  emit('update', profiles);
  deleteDialogRef.value?.close();
  profilePendingDeletion.value = null;
};

const cancelDeletion = () => {
  profilePendingDeletion.value = null;
};

watch(availablePlatforms, platforms => {
  if (!platforms.length) resetForm();
});
</script>

<template>
  <div class="flex flex-col gap-3 p-3">
    <div v-if="configuredProfiles.length" class="flex flex-col gap-1">
      <div
        v-for="profile in configuredProfiles"
        :key="profile.key"
        class="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-n-alpha-2"
      >
        <Icon :icon="profile.icon" class="size-4 shrink-0 text-n-slate-11" />
        <div class="min-w-0 flex-1">
          <p class="mb-0 text-xs text-n-slate-10">
            {{ platformLabel(profile.key) }}
          </p>
          <Input
            v-if="editingPlatform === profile.key"
            v-model="editingValue"
            :placeholder="
              t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.USERNAME_PLACEHOLDER')
            "
            @keyup.enter="saveProfile"
            @keyup.esc="cancelEditing"
          />
          <a
            v-else-if="profileLink(profile)"
            :href="profileLink(profile)"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="block truncate text-sm text-n-slate-12 hover:text-n-brand"
          >
            {{ profile.value }}
          </a>
          <p v-else class="mb-0 truncate text-sm text-n-slate-12">
            {{ profile.value }}
          </p>
        </div>
        <div
          v-if="editingPlatform === profile.key"
          class="flex shrink-0 items-center gap-0.5"
        >
          <NextButton
            v-tooltip.top="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.SAVE_EDIT')"
            :aria-label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.SAVE_EDIT')"
            icon="i-lucide-check"
            color="slate"
            variant="ghost"
            size="xs"
            :disabled="!editingValue.trim()"
            data-test-id="save-social-profile"
            @click="saveProfile"
          />
          <NextButton
            v-tooltip.top="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.CANCEL')"
            :aria-label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.CANCEL')"
            icon="i-lucide-x"
            color="slate"
            variant="ghost"
            size="xs"
            @click="cancelEditing"
          />
        </div>
        <div v-else class="flex shrink-0 items-center gap-0.5">
          <NextButton
            v-tooltip.top="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.EDIT')"
            :aria-label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.EDIT')"
            icon="i-lucide-pencil"
            color="slate"
            variant="ghost"
            size="xs"
            data-test-id="edit-social-profile"
            @click="startEditing(profile)"
          />
          <NextButton
            v-tooltip.top="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.DELETE')"
            :aria-label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.DELETE')"
            icon="i-lucide-trash"
            color="ruby"
            variant="ghost"
            size="xs"
            data-test-id="delete-social-profile"
            @click="requestDeletion(profile)"
          />
        </div>
        <span
          v-if="editingPlatform !== profile.key && profileLink(profile)"
          class="i-lucide-external-link size-3.5 shrink-0 text-n-slate-10"
        />
      </div>
    </div>

    <div v-if="isAdding" class="flex flex-col gap-2">
      <ComboBox
        v-model="selectedPlatform"
        :options="availablePlatforms"
        :placeholder="
          t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.SELECT_PLACEHOLDER')
        "
      />
      <Input
        v-model="profileValue"
        :placeholder="
          t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.USERNAME_PLACEHOLDER')
        "
        @keyup.enter="addProfile"
      />
      <div class="flex justify-end gap-2">
        <NextButton
          :label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.CANCEL')"
          color="slate"
          variant="ghost"
          size="sm"
          @click="resetForm"
        />
        <NextButton
          :label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.SAVE')"
          color="blue"
          size="sm"
          :disabled="!selectedPlatform || !profileValue.trim()"
          @click="addProfile"
        />
      </div>
    </div>

    <NextButton
      v-else-if="availablePlatforms.length && !editingPlatform"
      :label="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.ADD')"
      icon="i-lucide-plus"
      color="slate"
      variant="outline"
      size="sm"
      class="self-start"
      @click="startAdding"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      width="sm"
      :title="t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.DELETE_TITLE')"
      :description="
        t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.DELETE_DESCRIPTION', {
          platform: profilePendingDeletion
            ? platformLabel(profilePendingDeletion.key)
            : '',
        })
      "
      :confirm-button-label="
        t('CONVERSATION_SIDEBAR.SOCIAL_PROFILES.DELETE_CONFIRM')
      "
      confirm-button-icon="i-lucide-trash"
      @confirm="deleteProfile"
      @close="cancelDeletion"
    />
  </div>
</template>
